import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { HomeIcon, ChevronRightIcon, MagnifyingGlassIcon, HeartIcon, XMarkIcon, ChevronLeftIcon, PhotoIcon, BuildingOfficeIcon, UsersIcon, UserCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import useNetworkHierarchy from '../hooks/useNetworkHierarchy';

// Importa il componente SearchableSelect
const SearchableSelect = ({ options, value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  
  // Aggiorna le opzioni filtrate quando cambiano le opzioni o il termine di ricerca
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      setFilteredOptions(
        options.filter(option => 
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }
  }, [options, searchTerm]);
  
  // Calcola la posizione del dropdown quando si apre
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen]);
  
  // Gestisce il click fuori dal dropdown per chiuderlo
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        triggerRef.current && !triggerRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);
  
  // Gestisce il ridimensionamento della finestra
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    if (isOpen) {
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleResize);
      };
    }
  }, [isOpen]);
  
  // Trova l'etichetta corrente in base al valore
  const currentLabel = options.find(option => option.value === value)?.label || placeholder;
  
  const dropdownContent = isOpen ? (
    <div 
      ref={dropdownRef}
      className="fixed z-[99999] bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-xl shadow-2xl shadow-gray-500/25"
      style={{
        top: dropdownPosition.top,
        left: dropdownPosition.left,
        width: dropdownPosition.width,
        minWidth: '200px'
      }}
    >
      <div className="p-3 border-b border-gray-100/50">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Cerca..."
          className="w-full p-2 bg-gray-50/50 border border-gray-200/50 rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.key === 'Escape' && setIsOpen(false)}
          autoFocus
        />
      </div>
      <div className="max-h-48 overflow-y-auto">
        {filteredOptions.length === 0 ? (
          <div className="p-3 text-gray-500 text-center">Nessun risultato</div>
        ) : (
          filteredOptions.map((option) => (
            <div
              key={option.value}
              className={`p-3 cursor-pointer hover:bg-blue-50/50 transition-colors duration-200 ${value === option.value ? 'bg-blue-50/70 text-blue-700 font-medium' : 'text-gray-700'}`}
              onClick={() => {
                onChange({ target: { name, value: option.value } });
                setIsOpen(false);
                setSearchTerm('');
              }}
            >
              {option.label}
            </div>
          ))
        )}
      </div>
    </div>
  ) : null;
  
  return (
    <div className="relative w-full">
      <div 
        ref={triggerRef}
        className="w-full bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm p-3 cursor-pointer flex justify-between items-center transition-all duration-200 hover:bg-white/90"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={`${value ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
          {currentLabel}
        </span>
        <svg className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      
      {/* Renderizza il dropdown usando un portale */}
      {isOpen && ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

const OffertePublicPage = () => {
  const navigate = useNavigate();
  
  // Recupera utente corrente da localStorage
  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('user'));
  } catch (e) {
    user = null;
  }
  
  // Hook per gestire la gerarchia della rete
  const { userNode, subordinateNodes, loading: hierarchyLoading } = useNetworkHierarchy(user);
  
  const [offerte, setOfferte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vetture, setVetture] = useState([]);
  const [marche, setMarche] = useState([]);
  const [societaNoleggio, setSocietaNoleggio] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [filtroMarca, setFiltroMarca] = useState('');
  const [filtroAlimentazione, setFiltroAlimentazione] = useState('');
  const [filtroTipoCliente, setFiltroTipoCliente] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroSocietaNoleggio, setFiltroSocietaNoleggio] = useState('');
  const [filtroBudgetMin, setFiltroBudgetMin] = useState('');
  const [filtroBudgetMax, setFiltroBudgetMax] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleOfferte, setVehicleOfferte] = useState([]);
  const [currentFilterIndex, setCurrentFilterIndex] = useState(0);
  const [durataFilter, setDurataFilter] = useState('');
  const [kmAnnuiFilter, setKmAnnuiFilter] = useState('');
  const [tipoClienteFilter, setTipoClienteFilter] = useState('');
  // Nuovo stato per le scadenze
  const [scadenzeOfferte, setScadenzeOfferte] = useState({});
  const [loadingScadenze, setLoadingScadenze] = useState(false);
  // Stato per il filtro anticipo per valore
  const [anticipoFilter, setAnticipoFilter] = useState('');
  
  // Stati per selezione pratica
  const [showSelezionePraticaModal, setShowSelezionePraticaModal] = useState(false);
  const [pratiche, setPratiche] = useState([]);
  const [scaricandoPdf, setScaricandoPdf] = useState(false);
  const [loadingPratiche, setLoadingPratiche] = useState(false);
  const [praticaSelezionata, setPraticaSelezionata] = useState('');
  const [offertaPerPreventivo, setOffertaPerPreventivo] = useState(null);
  const [searchPratiche, setSearchPratiche] = useState('');
  
  // Stati per calcolo provvigioni
  const [showProvvigioni, setShowProvvigioni] = useState(false);
  const [provvigioniData, setProvvigioniData] = useState(null);
  const [loadingProvvigioni, setLoadingProvvigioni] = useState(false);
  
  // Stati per gestione cliente e pratica
  const [modalTab, setModalTab] = useState('pratica'); // 'pratica' o 'cliente'
  const [clienti, setClienti] = useState([]);
  const [loadingClienti, setLoadingClienti] = useState(false);
  const [clienteSelezionato, setClienteSelezionato] = useState('');
  const [searchClienti, setSearchClienti] = useState('');
  const [showNuovoClienteForm, setShowNuovoClienteForm] = useState(false);
  const [nuovoClienteData, setNuovoClienteData] = useState({
    tipo_cliente: 'privato',
    nome: '',
    cognome: '',
    ragione_sociale: '',
    codice_fiscale: '',
    partita_iva: '',
    email: '',
    telefono: '',
    indirizzo: '',
    cap: '',
    citta: '',
    provincia: ''
  });
  const [loadingCreazione, setLoadingCreazione] = useState(false);
  
  // Recupera le offerte disponibili
  useEffect(() => {
    const fetchOfferte = async () => {
      setLoading(true);
      try {
        console.log("Caricamento offerte...");
        const response = await api.get('/offerte-vetture?statoOfferta=true&limit=2000');
        console.log("Risposta API:", response.data);
        
        let offerteData = [];
        if (response.data && response.data.items) {
          // Nuovo formato con paginazione
          offerteData = response.data.items;
        } else if (response.data && response.data.data) {
          // Formato con all=true
          offerteData = response.data.data;
        } else if (response.data && response.data.offerte) {
          // Formato legacy
          offerteData = response.data.offerte;
        } else if (response.data && Array.isArray(response.data)) {
          // Array diretto
          offerteData = response.data;
        }
        
        console.log(`Trovate ${offerteData.length} offerte`);
        // Log per capire la struttura delle offerte e le proprietà disponibili
        if (offerteData.length > 0) {
          console.log("Struttura di un'offerta esempio:", offerteData[0]);
          console.log("Valori di tipoCliente nelle offerte:", [...new Set(offerteData.map(o => o.tipoCliente))]);
        }
        
        setOfferte(offerteData);
        
        // Filtriamo per tenere solo le offerte con vettura valida
        const offerteValide = offerteData.filter(offerta => offerta.vettura && offerta.vettura.id);
        
        // Estrai le vetture uniche dalle offerte
        const vettureUniche = [...new Map(
          offerteValide.map(offerta => [offerta.vettura.id, offerta.vettura])
        ).values()];
        
        console.log(`Trovate ${vettureUniche.length} vetture uniche`);
        setVetture(vettureUniche);
        
        // Estrai le marche uniche
        const marcheUniche = [...new Set(
          vettureUniche
            .filter(vettura => vettura.marca)
            .map(vettura => vettura.marca.descrizione)
        )].sort();
        
        console.log(`Trovate ${marcheUniche.length} marche uniche:`, marcheUniche);
        setMarche(marcheUniche);
        
        // Estrai le società di noleggio uniche
        const societaUniche = [...new Set(
          offerteData
            .filter(offerta => offerta.societaNoleggio && offerta.societaNoleggio.nome)
            .map(offerta => ({ 
              id: offerta.societaNoleggio.id, 
              nome: offerta.societaNoleggio.nome 
            }))
            .map(soc => JSON.stringify(soc))
        )].map(str => JSON.parse(str)).sort((a, b) => a.nome.localeCompare(b.nome));
        
        console.log(`Trovate ${societaUniche.length} società di noleggio uniche:`, societaUniche);
        setSocietaNoleggio(societaUniche);
        
        // Carica le categorie disponibili
        try {
          const categorieRes = await api.get('/categorie-offerte', {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (categorieRes.data && categorieRes.data.success) {
            setCategorie(categorieRes.data.data || []);
            console.log(`Caricate ${categorieRes.data.data?.length || 0} categorie`);
          }
        } catch (error) {
          console.error('Errore nel caricamento categorie:', error);
          setCategorie([]);
        }
        
        // Carica le scadenze DOPO un breve delay per permettere il rendering delle offerte
        setTimeout(() => {
          if (vettureUniche.length > 0) {
            console.log("Caricamento scadenze in background...");
            fetchScadenze(vettureUniche.map(v => v.id));
          }
        }, 500);
        
      } catch (error) {
        console.error('Errore nel caricamento delle offerte:', error);
        toast.error('Errore nel caricamento delle offerte');
      }
      setLoading(false);
    };

    fetchOfferte();
  }, []);
  
  // Funzione per recuperare le scadenze per tutte le vetture
  const fetchScadenze = async (vettureIds) => {
    if (!vettureIds || vettureIds.length === 0) return;
    
    setLoadingScadenze(true);
    try {
      // Creiamo un oggetto per memorizzare le scadenze per ID vettura
      const scadenze = {};
      
      // Processare le vetture in batch di 10 per non sovraccaricare il server
      const batchSize = 10;
      const batches = [];
      for (let i = 0; i < vettureIds.length; i += batchSize) {
        batches.push(vettureIds.slice(i, i + batchSize));
      }
      
      // Per ogni batch, recupera le scadenze con un piccolo delay
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];
        
        // Processa il batch in parallelo
        const batchPromises = batch.map(async (id) => {
          try {
            const response = await api.get(`/offerte-vetture/scadenze/vettura/${id}`);
            if (response.data) {
              scadenze[id] = response.data;
            }
          } catch (error) {
            // Ignora gli errori 404 (scadenza non trovata)
            if (error.response && error.response.status !== 404) {
              console.error(`Errore nel recupero della scadenza per la vettura ${id}:`, error);
            }
          }
        });
        
        await Promise.all(batchPromises);
        
        // Piccolo delay tra i batch (solo se non è l'ultimo)
        if (batchIndex < batches.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setScadenzeOfferte(scadenze);
    } catch (error) {
      console.error('Errore nel recupero delle scadenze:', error);
    } finally {
      setLoadingScadenze(false);
    }
  };
  
  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    });
  };
  
  // Funzione per formattare le date nella modale
  const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    });
  };
  
  // Filtra le offerte in base ai criteri selezionati
  const offerteFiltrate = offerte.filter(offerta => {
    // Verifica che l'offerta abbia vettura prima di filtrare
    if (!offerta.vettura) {
      console.log("Offerta senza vettura:", offerta.id);
      return false;
    }
    
    // Filtra per marca
    if (filtroMarca && offerta.vettura.marca && offerta.vettura.marca.descrizione !== filtroMarca) {
      return false;
    }
    
    // Filtra per alimentazione
    if (filtroAlimentazione && offerta.vettura.alimentazione !== filtroAlimentazione) {
      return false;
    }
    
    // Filtra per tipo cliente
    if (filtroTipoCliente) {
      // Gestiamo diversi possibili campi e valori per il tipo cliente
      const tipoClienteOfferta = offerta.tipoCliente || offerta.tipo_cliente;
      
      if (!tipoClienteOfferta) {
        return false; // Se non c'è tipo cliente e abbiamo un filtro, escludiamo
      }
      
      // Normalizza i valori per il confronto
      const tipoNormalizzato = tipoClienteOfferta.toLowerCase().trim();
      const filtroNormalizzato = filtroTipoCliente.toLowerCase().trim();
      
      // Logica di filtro per tipoCliente:
      // - Se selezioniamo "Privato" → mostrare offerte "Privato" e "Tutti"
      // - Se selezioniamo "Azienda" → mostrare offerte "Azienda" e "Tutti"
      if (filtroNormalizzato === 'privato') {
        if (tipoNormalizzato !== 'privato' && tipoNormalizzato !== 'tutti') {
          return false;
        }
      } else if (filtroNormalizzato === 'azienda') {
        if (tipoNormalizzato !== 'azienda' && tipoNormalizzato !== 'tutti') {
          return false;
        }
      } else if (tipoNormalizzato !== filtroNormalizzato) {
        return false;
      }
    }
    
    // Filtra per società di noleggio
    if (filtroSocietaNoleggio && offerta.societaNoleggio) {
      const societaId = parseInt(filtroSocietaNoleggio);
      if (offerta.societaNoleggio.id !== societaId) {
        return false;
      }
    }
    
    // Filtra per categoria
    if (filtroCategoria) {
      if (filtroCategoria === 'null') {
        // Mostra solo offerte senza categoria
        if (offerta.categoriaId !== null && offerta.categoriaId !== undefined) {
          return false;
        }
      } else {
        const categoriaSelezionata = parseInt(filtroCategoria);
        // Se l'offerta non ha categoria, non mostrarla quando si filtra per categoria specifica
        if (offerta.categoriaId === null || offerta.categoriaId === undefined) {
          return false;
        }
        // Se ha una categoria diversa da quella selezionata, non mostrarla
        if (offerta.categoriaId !== categoriaSelezionata) {
          return false;
        }
      }
    }
    
    // Filtra per budget (canone mensile)
    const canone = parseFloat(offerta.canone);
    if (filtroBudgetMin && canone < parseFloat(filtroBudgetMin)) {
      return false;
    }
    if (filtroBudgetMax && canone > parseFloat(filtroBudgetMax)) {
      return false;
    }
    
    // Filtra per ricerca testuale (modello, allestimento)
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const modello = offerta.vettura.modello ? offerta.vettura.modello.toLowerCase() : '';
      const allestimento = offerta.vettura.allestimento ? offerta.vettura.allestimento.toLowerCase() : '';
      const brand = offerta.vettura.brand ? offerta.vettura.brand.toLowerCase() : '';
      
      return modello.includes(query) || allestimento.includes(query) || brand.includes(query);
    }
    
    return true;
  });
  
  console.log(`Offerte filtrate: ${offerteFiltrate.length}`);
  
  // Raggruppa le offerte per vettura
  const offertePerVettura = offerteFiltrate.reduce((acc, offerta) => {
    if (!offerta.vettura || !offerta.vettura.id) return acc;
    
    const idVettura = offerta.vettura.id;
    
    if (!acc[idVettura]) {
      acc[idVettura] = {
        vettura: offerta.vettura,
        offerte: []
      };
    }
    
    acc[idVettura].offerte.push(offerta);
    return acc;
  }, {});
  
  // Trova l'offerta più economica per ogni vettura
  const vettureConOffertaMigliore = Object.values(offertePerVettura).map(gruppo => {
    if (!gruppo.offerte.length) return gruppo;
    
    const offertaMigliore = gruppo.offerte.reduce(
      (best, current) => (parseFloat(current.canone) < parseFloat(best.canone) ? current : best),
      gruppo.offerte[0]
    );
    
    return {
      ...gruppo,
      offertaMigliore
    };
  });
  
  // Funzione per formattare i prezzi
  const formatPrezzo = (prezzo) => {
    if (prezzo === undefined || prezzo === null) return '0 €';
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(prezzo);
  };
  
  // Aggiorna la funzione per ottenere l'URL dell'immagine
  const getVehicleImage = (vettura) => {
    if (!vettura) return null;
    
    // Se abbiamo l'oggetto immagine completo, usa il suo path
    if (vettura.immagine && vettura.immagine.path_file) {
      return `${window.location.origin}${vettura.immagine.path_file}`;
    }
    
    // Fallback per compatibilità: se img è un numero, è probabilmente un ID
    if (typeof vettura.img === 'number') {
      return null; 
    }
    
    // Se img è un path, usalo (caso legacy)
    if (typeof vettura.img === 'string') {
      if (vettura.img.startsWith('/')) {
        return `${window.location.origin}${vettura.img}`;
      } else if (vettura.img.startsWith('../')) {
        return vettura.img.replace('../', '/');
      }
      return vettura.img;
    }
    
    return null;
  };
  
  // Funzione per aprire la modale con le offerte della vettura
  const openPreventivatore = (vettura) => {
    setSelectedVehicle(vettura);
    
    // Recupera tutte le offerte per questa vettura
    const offerteVettura = offertePerVettura[vettura.id]?.offerte || [];
    //console.log(`Offerte per la vettura ${vettura.id}:`, offerteVettura.length);
    
    // Estrai valori unici per i filtri
    const durateUniche = [...new Set(offerteVettura.map(o => o.durata))].sort((a, b) => a - b);
    const kmAnnuiUnici = [...new Set(offerteVettura.map(o => o.kmAnnui))].sort((a, b) => a - b);
    
    // Imposta i filtri predefiniti se disponibili
    setDurataFilter(durateUniche.length > 0 ? durateUniche[0].toString() : '');
    setKmAnnuiFilter(kmAnnuiUnici.length > 0 ? kmAnnuiUnici[0].toString() : '');
    
    // Filtriamo le offerte per il tipo cliente già selezionato globalmente
    let offerteFiltratePerTipoCliente = offerteVettura;
    if (filtroTipoCliente) {
      const filtroNormalizzato = filtroTipoCliente.toLowerCase().trim();
      
      offerteFiltratePerTipoCliente = offerteVettura.filter(offerta => {
        const tipoClienteOfferta = (offerta.tipoCliente || '').toLowerCase().trim();
        
        // Mostra offerte che corrispondono al tipo cliente o sono "Tutti"
        return tipoClienteOfferta === filtroNormalizzato || tipoClienteOfferta === 'tutti';
      });
      
      // Se non ci sono offerte dopo il filtro, usiamo tutte le offerte disponibili
      if (offerteFiltratePerTipoCliente.length === 0) {
        offerteFiltratePerTipoCliente = offerteVettura;
      }
    }
    
    // Usa il filtro tipo cliente globale
    setTipoClienteFilter(filtroTipoCliente || '');
    
    setVehicleOfferte(offerteFiltratePerTipoCliente);
    setCurrentFilterIndex(0);
    setShowModal(true);
  };
  
  // Funzione per filtrare le offerte nella modale
  const getFilteredOfferte = () => {
    return vehicleOfferte.filter(offerta => 
      (durataFilter ? offerta.durata.toString() === durataFilter : true) &&
      (kmAnnuiFilter ? offerta.kmAnnui.toString() === kmAnnuiFilter : true) &&
      (anticipoFilter !== '' && anticipoFilter !== null && anticipoFilter !== undefined ? 
        (offerta.anticipo || 0).toString() === anticipoFilter : true)
    );
  };
  
  // Ordinamento delle offerte filtrate per canone
  const getFilteredAndSortedOfferte = () => {
    const filtered = getFilteredOfferte();
    return filtered.sort((a, b) => parseFloat(a.canone) - parseFloat(b.canone));
  };
  
  // Ottenere l'offerta corrente basata sui filtri
  const getCurrentOfferta = () => {
    const filteredOfferte = getFilteredAndSortedOfferte();
    if (filteredOfferte.length === 0) return null;
    
    if (currentFilterIndex >= filteredOfferte.length) {
      setCurrentFilterIndex(0);
    }
    
    return filteredOfferte[currentFilterIndex];
  };
  
  // Funzione per formattare i valori delle penali
  const formatPenaleValue = (value) => {
    if (!value || value === 0) return 'Nessuna';
    
    // Se è un valore negativo, è una percentuale
    if (value < 0) {
      return `${Math.abs(value)}%`;
    }
    
    // Altrimenti è un valore in euro
    return `${value}€`;
  };
  
  // Navigazione tra le offerte
  const navigateOfferte = (direction) => {
    const filteredOfferte = getFilteredOfferte();
    if (filteredOfferte.length <= 1) return;
    
    if (direction === 'next') {
      setCurrentFilterIndex((currentFilterIndex + 1) % filteredOfferte.length);
    } else {
      setCurrentFilterIndex(currentFilterIndex === 0 ? filteredOfferte.length - 1 : currentFilterIndex - 1);
    }
    
    // Reset stati provvigioni quando cambia offerta
    setShowProvvigioni(false);
    setProvvigioniData(null);
  };
  
  // Funzione per interpretare i servizi
  const getServiziSelezionati = (serviziString) => {
    if (!serviziString) return [];
    try {
      return JSON.parse(serviziString);
    } catch (error) {
      console.error('Errore nel parsing dei servizi:', error);
      return [];
    }
  };

  // Prepara le opzioni per le select
  const getMarcheOptions = () => {
    return [
      { value: '', label: 'Tutte le marche' },
      ...marche.map(marca => ({ value: marca, label: marca }))
    ];
  };

  const getSocietaNoleggioOptions = () => {
    return [
      { value: '', label: 'Tutte le società' },
      ...societaNoleggio.map(societa => ({ value: societa.id.toString(), label: societa.nome }))
    ];
  };

  const getAlimentazioniOptions = () => {
    return [
      { value: '', label: 'Tutte le alimentazioni' },
      { value: 'Benzina', label: 'Benzina' },
      { value: 'Diesel', label: 'Diesel' },
      { value: 'Elettrica', label: 'Elettrica' },
      { value: 'Ibrida Benzina', label: 'Ibrida Benzina' },
      { value: 'Ibrida Diesel', label: 'Ibrida Diesel' },
      { value: 'Plugin Hybrid', label: 'Plugin Hybrid' }
    ];
  };

  const getCategorieOptions = () => {
    return [
      { value: '', label: 'Tutte le categorie' },
      ...categorie
        .filter(categoria => categoria.attiva)
        .sort((a, b) => a.ordinamento - b.ordinamento)
        .map(categoria => ({ 
          value: categoria.id.toString(), 
          label: categoria.nome,
          color: categoria.colore
        })),
      { value: 'null', label: 'Senza categoria', color: '#9CA3AF' }
    ];
  };

  const getTipiClienteOptions = () => {
    return [
      { value: '', label: 'Tutti i tipi' },
      { value: 'Privato', label: 'Privato' },
      { value: 'Azienda', label: 'Azienda' }
    ];
  };

  // Funzioni per gestione selezione pratica
  const fetchPratiche = async () => {
    setLoadingPratiche(true);
    try {
      // Costruisci i parametri usando URLSearchParams per gestire array
      const params = new URLSearchParams();
      params.append('limit', '1000');
      
      // Solo pratiche in stati che accettano nuovi preventivi
      const statiValidi = ['pratica_avviata', 'attesa_preventivo', 'valutazione_cliente', 'attesa_approvazione_cliente'];
      statiValidi.forEach(stato => {
        params.append('stato_in', stato);
      });
      
      console.log('🔍 Caricamento pratiche con filtri di rete per utente:', user?.id, user?.role);
      
      const response = await api.get(`/pratiche?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      let praticheData = [];
      if (response.data && response.data.items) {
        praticheData = response.data.items;
      } else if (response.data && Array.isArray(response.data)) {
        praticheData = response.data;
      }
      
      //console.log(`✅ Caricate ${praticheData.length} pratiche filtrate per la rete`);
      setPratiche(praticheData);
    } catch (error) {
      console.error('Errore nel caricamento pratiche:', error);
      toast.error('Errore nel caricamento delle pratiche');
    } finally {
      setLoadingPratiche(false);
    }
  };

  // Scarica il preventivo PDF dell'offerta selezionata, usando il template branding dell'agenzia
  const scaricaPreventivoOffertaPdf = async () => {
    const offertaSelezionata = getCurrentOfferta();
    if (!offertaSelezionata?.id) {
      toast.warning('Seleziona prima un\'offerta valida.');
      return;
    }
    setScaricandoPdf(true);
    try {
      const resp = await api.get(`/pratica/preventivo-offerta/${offertaSelezionata.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preventivo_offerta_${offertaSelezionata.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      let msg = 'Errore nella generazione del preventivo PDF';
      try {
        if (err.response?.data && typeof err.response.data.text === 'function') {
          const j = JSON.parse(await err.response.data.text());
          msg = j.error || msg;
        }
      } catch (e) { /* ignora */ }
      toast.error(msg);
    } finally {
      setScaricandoPdf(false);
    }
  };

  const openSelezionePraticaModal = (offerta) => {
    setOffertaPerPreventivo(offerta);
    setShowSelezionePraticaModal(true);
    setPraticaSelezionata('');
    setSearchPratiche('');
    setModalTab('pratica');
    setClienteSelezionato('');
    setSearchClienti('');
    setShowNuovoClienteForm(false);
    fetchPratiche();
  };

  // Funzioni per gestione clienti
  const fetchClienti = async () => {
    setLoadingClienti(true);
    try {
      console.log('🔍 Caricamento clienti con filtri di rete per utente:', user?.id, user?.role);
      
      const response = await api.get('/clienti?limit=1000', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      let clientiData = [];
      if (response.data && response.data.clients) {
        clientiData = response.data.clients;
      } else if (response.data && response.data.items) {
        clientiData = response.data.items;
      } else if (response.data && Array.isArray(response.data)) {
        clientiData = response.data;
      }
      
      console.log(`✅ Caricati ${clientiData.length} clienti filtrati per la rete`);
      setClienti(clientiData);
    } catch (error) {
      console.error('Errore nel caricamento clienti:', error);
      toast.error('Errore nel caricamento dei clienti');
    } finally {
      setLoadingClienti(false);
    }
  };

  const verificaPraticheCliente = async (clienteId) => {
    try {
      // Cerca solo pratiche in stati che accettano nuovi preventivi
      const params = new URLSearchParams();
      params.append('cliente_id', clienteId);
      const statiValidi = ['pratica_avviata', 'attesa_preventivo', 'valutazione_cliente', 'attesa_approvazione_cliente'];
      statiValidi.forEach(stato => {
        params.append('stato_in', stato);
      });
      
      const response = await api.get(`/pratiche?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data && response.data.items && response.data.items.length > 0) {
        return response.data.items[0]; // Restituisce la prima pratica valida
      }
      return null;
    } catch (error) {
      console.error('Errore nella verifica pratiche cliente:', error);
      return null;
    }
  };

  const creaPratica = async (clienteId) => {
    try {
      // Determina l'assegnazione in base al ruolo dell'utente
      let praticaData = {
        cliente_id: clienteId,
        stato_pratica: 'pratica_avviata',
        note: 'Pratica creata automaticamente dal catalogo offerte'
      };

      if (user && userNode) {
        const userRole = user.role || user.ruolo;
        console.log('==============================');
        console.log('DEBUG CREAZIONE PRATICA');
        console.log('Utente:', user);
        console.log('Nodo utente:', userNode);
        console.log('Ruolo utente:', userRole);
        
        if (userRole === 'agenzia') {
          praticaData.idAgenziaPrimaria = userNode.id;
          praticaData.idAgente = null;
          console.log('Agenzia crea pratica - idAgenziaPrimaria:', userNode.id, 'idAgente: null');
        } else if (userRole === 'subagente' || userRole === 'agente') {
          praticaData.idAgenziaPrimaria = userNode.parentId || userNode.id;
          praticaData.idAgente = user.id;
          console.log('Agente crea pratica - idAgenziaPrimaria:', userNode.parentId || userNode.id, 'idAgente:', user.id);
        } else {
          console.log('Ruolo non riconosciuto:', userRole, '- assegnazione standard');
        }
      } else {
        console.log('Dati utente o nodo mancanti - assegnazione standard');
      }

      console.log('Dati finali per creazione pratica:', praticaData);
      console.log('==============================');

      const response = await api.post('/pratica', praticaData);
      console.log('✅ Pratica creata con successo:', response.data);
      
      // Gestisce sia la nuova struttura che quella vecchia per retrocompatibilità
      return response.data.pratica || response.data;
    } catch (error) {
      console.error('Errore nella creazione pratica:', error);
      throw error;
    }
  };

  const creaCliente = async () => {
    try {
      // Prepara i dati del cliente con il responsabile assegnato all'utente corrente
      const clienteData = {
        ...nuovoClienteData,
        userId: user?.id, // Utente che ha creato il cliente
        responsabileId: user?.id // Responsabile del cliente (stesso utente che lo crea)
      };
      
      console.log('🔍 Creazione cliente con dati:', {
        ...clienteData,
        // Non mostrare dati sensibili nel log
        nome: clienteData.nome,
        cognome: clienteData.cognome,
        email: clienteData.email,
        userId: clienteData.userId,
        responsabileId: clienteData.responsabileId
      });
      
      const response = await api.post('/clienti', clienteData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('✅ Cliente creato con successo:', response.data?.id);
      return response.data;
    } catch (error) {
      console.error('Errore nella creazione cliente:', error);
      throw error;
    }
  };

  const associaOffertaAPratica = async () => {
    if (!praticaSelezionata || !offertaPerPreventivo) {
      toast.error('Seleziona una pratica');
      return;
    }

    setLoadingCreazione(true);
    try {
      const response = await api.post(`/pratica/${praticaSelezionata}/preventivi`, {
        offertaData: offertaPerPreventivo,
        tipoPreventivo: 'alternativo',
        note: `Preventivo creato tramite catalogo: ${offertaPerPreventivo.vettura?.marca?.descrizione || offertaPerPreventivo.vettura?.brand} ${offertaPerPreventivo.vettura?.modello} - €${offertaPerPreventivo.canone}/mese`,
        creatoDA: 'Catalogo Pubblico'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        toast.success('Preventivo associato alla pratica con successo!');
        setShowSelezionePraticaModal(false);
        setShowModal(false);
        setSelectedVehicle(null);
        setVehicleOfferte([]);
        setDurataFilter('');
        setKmAnnuiFilter('');
        setTipoClienteFilter('');
        setAnticipoFilter('');
        setCurrentFilterIndex(0);
        setShowProvvigioni(false);
        setProvvigioniData(null);
        navigate(`/nlt/pratiche/${praticaSelezionata}`);
      }
    } catch (error) {
      console.error('Errore nell\'associazione del preventivo:', error);
      toast.error('Errore nell\'associazione del preventivo alla pratica');
    } finally {
      setLoadingCreazione(false);
    }
  };

  // Filtra pratiche per la ricerca
  const praticheFiltered = pratiche.filter(pratica => {
    if (!searchPratiche) return true;
    const search = searchPratiche.toLowerCase();
    return (
      pratica.codicePratica?.toLowerCase().includes(search) ||
      pratica.cliente?.nome?.toLowerCase().includes(search) ||
      pratica.cliente?.cognome?.toLowerCase().includes(search) ||
      pratica.cliente?.ragione_sociale?.toLowerCase().includes(search)
    );
  });

  // Filtra clienti per la ricerca
  const clientiFiltered = clienti.filter(cliente => {
    if (!searchClienti) return true;
    const search = searchClienti.toLowerCase();
    return (
      cliente.nome?.toLowerCase().includes(search) ||
      cliente.cognome?.toLowerCase().includes(search) ||
      cliente.ragione_sociale?.toLowerCase().includes(search) ||
      cliente.email?.toLowerCase().includes(search) ||
      cliente.codice_fiscale?.toLowerCase().includes(search)
    );
  });

  const handleTabChange = (tab) => {
    setModalTab(tab);
    if (tab === 'cliente' && clienti.length === 0) {
      fetchClienti();
    }
  };

  const associaOffertaACliente = async () => {
    if (!clienteSelezionato || !offertaPerPreventivo) {
      toast.error('Seleziona un cliente');
      return;
    }

    setLoadingCreazione(true);
    try {
      // Verifica se il cliente ha già una pratica valida
      let praticaId = null;
      const praticaEsistente = await verificaPraticheCliente(clienteSelezionato);
      
      if (praticaEsistente) {
        praticaId = praticaEsistente.id;
        toast.info('Utilizzando pratica esistente del cliente');
      } else {
        // Crea una nuova pratica per il cliente
        const nuovaPratica = await creaPratica(clienteSelezionato);
        praticaId = nuovaPratica.id;
        toast.success('Nuova pratica creata per il cliente');
      }

      // Associa il preventivo alla pratica
      const response = await api.post(`/pratica/${praticaId}/preventivi`, {
        offertaData: offertaPerPreventivo,
        tipoPreventivo: 'alternativo',
        note: `Preventivo creato tramite catalogo: ${offertaPerPreventivo.vettura?.marca?.descrizione || offertaPerPreventivo.vettura?.brand} ${offertaPerPreventivo.vettura?.modello} - €${offertaPerPreventivo.canone}/mese`,
        creatoDA: 'Catalogo Pubblico'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        toast.success('Preventivo associato con successo!');
        setShowSelezionePraticaModal(false);
        setShowModal(false);
        setSelectedVehicle(null);
        setVehicleOfferte([]);
        setDurataFilter('');
        setKmAnnuiFilter('');
        setTipoClienteFilter('');
        setAnticipoFilter('');
        setCurrentFilterIndex(0);
        setShowProvvigioni(false);
        setProvvigioniData(null);
        navigate(`/nlt/pratiche/${praticaId}`);
      }
    } catch (error) {
      console.error('Errore nell\'associazione:', error);
      toast.error('Errore nell\'associazione del preventivo');
    } finally {
      setLoadingCreazione(false);
    }
  };

  const creaClienteEPratica = async () => {
    // Validazioni base
    if (nuovoClienteData.tipo_cliente === 'privato') {
      if (!nuovoClienteData.nome || !nuovoClienteData.cognome) {
        toast.error('Nome e cognome sono obbligatori per clienti privati');
        return;
      }
    } else {
      if (!nuovoClienteData.ragione_sociale) {
        toast.error('Ragione sociale è obbligatoria per aziende');
        return;
      }
    }

    if (!nuovoClienteData.email || !nuovoClienteData.telefono) {
      toast.error('Email e telefono sono obbligatori');
      return;
    }

    // Validazione email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(nuovoClienteData.email)) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    // Validazione codice fiscale
    if (nuovoClienteData.codice_fiscale && nuovoClienteData.codice_fiscale.length !== 16) {
      toast.error('Il codice fiscale deve essere di 16 caratteri');
      return;
    }

    // Validazione partita IVA per aziende
    if (nuovoClienteData.tipo_cliente === 'azienda' && nuovoClienteData.partita_iva) {
      if (nuovoClienteData.partita_iva.length !== 11 || !/^\d{11}$/.test(nuovoClienteData.partita_iva)) {
        toast.error('La partita IVA deve essere di 11 cifre');
        return;
      }
    }

    setLoadingCreazione(true);
    try {
      // Crea il nuovo cliente
      const nuovoCliente = await creaCliente();
      toast.success('Cliente creato con successo');

      // Crea una nuova pratica per il cliente
      const nuovaPratica = await creaPratica(nuovoCliente.id);
      toast.success('Pratica creata con successo');

      // Associa il preventivo alla pratica
      const response = await api.post(`/pratica/${nuovaPratica.id}/preventivi`, {
        offertaData: offertaPerPreventivo,
        tipoPreventivo: 'alternativo',
        note: `Preventivo creato tramite catalogo: ${offertaPerPreventivo.vettura?.marca?.descrizione || offertaPerPreventivo.vettura?.brand} ${offertaPerPreventivo.vettura?.modello} - €${offertaPerPreventivo.canone}/mese`,
        creatoDA: 'Catalogo Pubblico'
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        toast.success('Preventivo associato con successo!');
        setShowSelezionePraticaModal(false);
        setShowModal(false);
        setSelectedVehicle(null);
        setVehicleOfferte([]);
        setDurataFilter('');
        setKmAnnuiFilter('');
        setTipoClienteFilter('');
        setAnticipoFilter('');
        setCurrentFilterIndex(0);
        setShowProvvigioni(false);
        setProvvigioniData(null);
        navigate(`/nlt/pratiche/${nuovaPratica.id}`);
      }
    } catch (error) {
      console.error('Errore nella creazione:', error);
      toast.error('Errore nella creazione del cliente e pratica');
    } finally {
      setLoadingCreazione(false);
    }
  };

  // Funzione per calcolare le provvigioni dell'offerta corrente
  const calcolaProvvigioni = async () => {
    const offertaCorrente = getCurrentOfferta();
    if (!offertaCorrente) {
      toast.warning('Nessuna offerta selezionata per il calcolo provvigioni');
      return;
    }

    setLoadingProvvigioni(true);
    try {
      // Simula un contratto temporaneo per il calcolo delle provvigioni
      const contrattoTemp = {
        id: `temp_${Date.now()}`,
        canone: offertaCorrente.canone,
        durata: offertaCorrente.durata,
        provvigione: offertaCorrente.provvigione || 0,
        offertaId: offertaCorrente.id,
        tipoNodo: 'agenzia_primaria', // Tipo di default per l'utente corrente
        importoTotale: offertaCorrente.canone * offertaCorrente.durata
      };

      console.log('📊 Calcolo provvigioni per offerta:', {
        canone: contrattoTemp.canone,
        durata: contrattoTemp.durata,
        provvigione: contrattoTemp.provvigione,
        importoTotale: contrattoTemp.importoTotale
      });

      const response = await api.post(
        '/provvigioni-contratti/calcola-distribuzione',
        { contratto: contrattoTemp },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.success) {
        setProvvigioniData(response.data.data);
        console.log('✅ Provvigioni calcolate:', response.data.data);
      } else {
        throw new Error(response.data.message || 'Errore nel calcolo delle provvigioni');
      }
    } catch (error) {
      console.error('❌ Errore calcolo provvigioni:', error);
      toast.error('Errore nel calcolo delle provvigioni: ' + (error.response?.data?.message || error.message));
      setProvvigioniData({
        errore: 'Impossibile calcolare le provvigioni al momento',
        distribuzione: [],
        provvigioneTotale: 0
      });
    } finally {
      setLoadingProvvigioni(false);
    }
  };

  // Funzione per formattare la valuta (copiata da ProvvigioniContrattiPage)
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '€ 0,00';
    }
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Funzione per gestire il toggle della visualizzazione provvigioni
  const toggleProvvigioni = async () => {
    if (!showProvvigioni) {
      setShowProvvigioni(true);
      // Ricalcola sempre le provvigioni per l'offerta corrente
      await calcolaProvvigioni();
    } else {
      setShowProvvigioni(false);
    }
  };

  // Funzione per ottenere info sui tipi di nodo (copiata da ProvvigioniContrattiPage)
  const getTipoNodoInfo = (tipo) => {
    const iconClasses = "h-4 w-4";
    
    switch (tipo) {
      case 'agenzia_primaria':
        return {
          label: 'Agenzia Primaria',
          icon: <BuildingOfficeIcon className={iconClasses} />,
          color: 'bg-blue-100 text-blue-800'
        };
      case 'agenzia_secondaria':
        return {
          label: 'Agenzia Secondaria',
          icon: <BuildingOfficeIcon className={iconClasses} />,
          color: 'bg-green-100 text-green-800'
        };
      case 'referente':
        return {
          label: 'Referente',
          icon: <UsersIcon className={iconClasses} />,
          color: 'bg-yellow-100 text-yellow-800'
        };
      case 'sub_agente':
        return {
          label: 'Sub Agente',
          icon: <UserCircleIcon className={iconClasses} />,
          color: 'bg-purple-100 text-purple-800'
        };
      default:
        return {
          label: tipo || 'N/D',
          icon: <UserCircleIcon className={iconClasses} />,
          color: 'bg-gray-100 text-gray-800'
        };
    }
  };

  // Categoria "Usato": individua l'id dalla lista categorie e verifica l'appartenenza di un'offerta
  const catUsato = categorie.find(c => (c.nome || '').toLowerCase().trim() === 'usato');
  const isUsato = (o) => !!catUsato && o && Number(o.categoriaId) === Number(catUsato.id);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30">
      <div className="max-w-[1800px] mx-auto px-4 md:px-6 py-8 space-y-6">
        
        {/* Breadcrumb */}
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="flex space-x-4 items-center">
            <li>
              <div>
                <Link to="/nlt/" className="text-gray-400 hover:text-blue-600 transition-colors duration-200">
                  <HomeIcon className="flex-shrink-0 h-5 w-5" aria-hidden="true" />
                  <span className="sr-only">Home</span>
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon
                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <Link
                  to="/nlt/quotazioni"
                  className="ml-4 text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors duration-200"
                >
                  Quotazioni
                </Link>
              </div>
            </li>
            <li>
              <div className="flex items-center">
                <ChevronRightIcon
                  className="flex-shrink-0 h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
                <span className="ml-4 text-sm font-medium text-gray-600">
                  Offerte Noleggio LT
                </span>
              </div>
            </li>
          </ol>
        </nav>

        {/* Header */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 p-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Offerte Noleggio a Lungo Termine
              </h2>
              <p className="mt-2 text-gray-600">
                Visualizza e configura le offerte di noleggio a lungo termine disponibili pubblicamente
              </p>
            </div>
          </div>
        </div>

        {/* Filtri */}
        <div className="bg-white/80 backdrop-blur-xl shadow-lg shadow-gray-200/20 rounded-2xl border border-gray-100/50 overflow-hidden">
          <div className="bg-gradient-to-r from-gray-50/50 to-white/50 p-6 border-b border-gray-100/50">
            <div className="mb-6 flex flex-col lg:flex-row gap-4 justify-between items-center">
              {/* Barra di ricerca */}
              <div className="w-full lg:w-2/3 relative">
                <input
                  type="text"
                  placeholder="Cerca modello o allestimento..."
                  className="w-full pl-12 pr-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 placeholder-gray-400"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-4 top-1/2 transform -translate-y-1/2" />
              </div>
              
              {/* Filtro Tipo Cliente riposizionato vicino al campo di ricerca */}
              <div className="w-full lg:w-1/3 flex items-center space-x-3">
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Tipo Cliente:</span>
                <div className="flex-1">
                  <select
                    className="block w-full px-4 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 font-medium text-gray-700"
                    value={filtroTipoCliente}
                    onChange={(e) => setFiltroTipoCliente(e.target.value)}
                  >
                    <option value="">Tutti</option>
                    <option value="Privato">Privato</option>
                    <option value="Azienda">Azienda</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Filtro marca con SearchableSelect */}
              <div className="w-full">
                <SearchableSelect
                  options={getMarcheOptions()}
                  value={filtroMarca}
                  onChange={(e) => setFiltroMarca(e.target.value)}
                  placeholder="Tutte le marche"
                  name="filtroMarca"
                />
              </div>
              
              {/* Filtro alimentazione con SearchableSelect */}
              <div className="w-full">
                <SearchableSelect
                  options={getAlimentazioniOptions()}
                  value={filtroAlimentazione}
                  onChange={(e) => setFiltroAlimentazione(e.target.value)}
                  placeholder="Alimentazione"
                  name="filtroAlimentazione"
                />
              </div>
              
              {/* Filtro categoria con SearchableSelect */}
              <div className="w-full">
                <SearchableSelect
                  options={getCategorieOptions()}
                  value={filtroCategoria}
                  onChange={(e) => setFiltroCategoria(e.target.value)}
                  placeholder="Categoria"
                  name="filtroCategoria"
                />
              </div>
              
              {/* Filtro società di noleggio con SearchableSelect */}
              <div className="w-full">
                <SearchableSelect
                  options={getSocietaNoleggioOptions()}
                  value={filtroSocietaNoleggio}
                  onChange={(e) => setFiltroSocietaNoleggio(e.target.value)}
                  placeholder="Società di noleggio"
                  name="filtroSocietaNoleggio"
                />
              </div>
              
              {/* Filtro budget */}
              <div className="flex space-x-3 items-center">
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 font-medium">€</span>
                  <input
                    type="number"
                    placeholder="Da"
                    className="w-full pl-8 pr-3 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 placeholder-gray-400"
                    value={filtroBudgetMin}
                    onChange={(e) => setFiltroBudgetMin(e.target.value)}
                  />
                </div>
                <span className="text-gray-500 font-medium">-</span>
                <div className="relative flex-1">
                  <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-500 font-medium">€</span>
                  <input
                    type="number"
                    placeholder="A"
                    className="w-full pl-8 pr-3 py-3 bg-white/70 backdrop-blur-sm border border-gray-200/50 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/50 transition-all duration-200 placeholder-gray-400"
                    value={filtroBudgetMax}
                    onChange={(e) => setFiltroBudgetMax(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Numero risultati */}
        <div className="px-2">
          <span className="text-lg font-bold text-gray-900">
            {vettureConOffertaMigliore.length} 
            <span className="font-medium text-gray-600 ml-2">
              {vettureConOffertaMigliore.length === 1 ? 'Risultato' : 'Risultati'}
            </span>
          </span>
        </div>
        
        {/* Griglia offerte */}
        <div className="pb-12">
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-2 border-blue-200 border-t-blue-600"></div>
              <p className="mt-6 text-gray-600 font-medium">Caricamento offerte...</p>
            </div>
          ) : vettureConOffertaMigliore.length === 0 ? (
            <div className="text-center py-16 bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/20 border border-gray-100/50">
              <div className="mx-auto h-16 w-16 text-gray-400 mb-6">
                <PhotoIcon className="h-full w-full" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Nessuna offerta trovata</h3>
              <p className="text-gray-600">Prova a modificare i filtri di ricerca per vedere più risultati.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {vettureConOffertaMigliore.map(({ vettura, offertaMigliore }) => (
                <div key={vettura.id} className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg shadow-gray-200/20 border border-gray-100/50 overflow-hidden hover:shadow-xl hover:shadow-gray-200/30 transform hover:scale-105 transition-all duration-300 group">
                  {/* Badge offerta */}
                  <div className="relative">
                    
                    {/* Immagine auto - migliorata come in GestioneOfferteVetturePage */}
                    <div className="bg-gradient-to-br from-blue-50/50 to-blue-100/30 p-6 h-48 relative overflow-hidden">
                      {getVehicleImage(vettura) ? (
                        <img
                          src={getVehicleImage(vettura)}
                          alt={`${vettura.marca?.descrizione || vettura.brand || ''} ${vettura.modello || ''}`}
                          className="w-full h-full object-contain absolute inset-0 p-2 group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="h-20 w-20 bg-blue-200/50 backdrop-blur-sm rounded-full flex items-center justify-center">
                            <PhotoIcon className="h-10 w-10 text-blue-500" />
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Badge tipo cliente (Privato/Azienda) */}
                    {['privato', 'azienda'].includes((offertaMigliore.tipoCliente || offertaMigliore.tipo_cliente || '').toLowerCase()) && (
                      <div className="absolute top-0 left-0 bg-gradient-to-r from-amber-500 to-amber-600 text-white px-3 py-1 rounded-br-xl font-semibold text-xs shadow-lg">
                        {(offertaMigliore.tipoCliente || offertaMigliore.tipo_cliente || '').toLowerCase() === 'privato' ? 'Offerta per privato' : 'Offerta per azienda'}
                      </div>
                    )}

                    {/* Badge categoria Usato */}
                    {isUsato(offertaMigliore) && (
                      <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-600 to-purple-700 text-white px-3 py-1 rounded-bl-xl font-semibold text-xs shadow-lg">
                        Usato
                      </div>
                    )}

                    {/* Badge stato disponibilità */}
                    <div className="absolute bottom-0 right-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-tl-xl font-semibold text-sm shadow-lg">
                      {offertaMigliore.statoDisponibilita || 'Nuovo'}
                    </div>
                  </div>
                  
                  {/* Informazioni vettura */}
                  <div className="px-6 py-4 border-b border-gray-100/50">
                    <div className="uppercase font-bold text-gray-900 group-hover:text-blue-700 transition-colors duration-200">
                      {vettura.marca?.descrizione || vettura.brand || ''} {vettura.modello || ''}
                    </div>
                    <div className="text-sm text-gray-600 font-medium">
                      {vettura.allestimento || ''}
                    </div>
                  </div>
                  
                  {/* Prezzo */}
                  <div className="px-6 py-4 bg-gradient-to-r from-blue-50/50 to-blue-100/30 backdrop-blur-sm">
                    <div className="flex items-center justify-between">
                      <div className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
                        {formatPrezzo(offertaMigliore.canone)}
                        <span className="text-sm font-normal text-blue-600">/mese IVA escl.</span>
                      </div>
                    </div>
                    <div className="text-xs text-blue-700 uppercase font-bold mt-1 tracking-wide">
                      Offerta limitata
                    </div>
                  </div>
                  
                  {/* Dettagli */}
                  <div className="px-6 py-4 text-sm text-gray-600">
                    <div className="flex justify-between font-medium">
                      <span>{offertaMigliore.durata} Mesi</span>
                      <span>{parseInt(offertaMigliore.kmAnnui).toLocaleString()} Km/Anno</span>
                      <span>Anticipo</span>
                    </div>
                    <div className="mt-2 text-center">
                      <span className="text-lg font-bold text-gray-900">
                        € {offertaMigliore.anticipo ? parseInt(offertaMigliore.anticipo).toLocaleString() : '0'}
                      </span>
                      <span className="text-xs text-gray-500 ml-1">IVA Escl.</span>
                    </div>
                  </div>
                  
                  {/* Pulsante */}
                  <div className="px-6 py-4">
                    <button 
                      onClick={() => openPreventivatore(vettura)}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 px-4 rounded-xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transform hover:scale-105 transition-all duration-200"
                    >
                      Scopri l'offerta
                    </button>
                    <div className="text-center text-xs text-gray-500 mt-3 font-medium">
                      {scadenzeOfferte[vettura.id] ? `Fino al ${formatDate(scadenzeOfferte[vettura.id].scadenzaOfferte)}` : ''}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Modale Preventivatore */}
        {showModal && selectedVehicle && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Overlay */}
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
              
              {/* Modal */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
                {/* Header */}
                <div className="bg-white px-4 pt-4 pb-4 sm:p-6 sm:pb-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Configura il noleggio - {selectedVehicle.marca?.descrizione || selectedVehicle.brand} {selectedVehicle.modello} {selectedVehicle.allestimento}
                    </h3>
                    <button
                      onClick={() => {
                        setShowModal(false);
                        setSelectedVehicle(null);
                        setVehicleOfferte([]);
                        setDurataFilter('');
                        setKmAnnuiFilter('');
                        setTipoClienteFilter('');
                        setAnticipoFilter('');
                        setCurrentFilterIndex(0);
                        setShowProvvigioni(false);
                        setProvvigioniData(null);
                      }}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
                
                {/* Body */}
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  <div className="sm:flex sm:items-start">
                    {/* Colonna sinistra: Immagine auto + Caratteristiche veicolo */}
                    <div className="sm:w-1/3 mb-4 sm:mb-0 sm:mr-4">
                      {/* Immagine auto */}
                      <div className="bg-blue-50 rounded-lg p-4 relative min-h-[150px] mb-4">
                        {getVehicleImage(selectedVehicle) ? (
                          <img
                            src={getVehicleImage(selectedVehicle)}
                            alt={`${selectedVehicle.marca?.descrizione || selectedVehicle.brand || ''} ${selectedVehicle.modello || ''}`}
                            className="w-full h-full object-contain absolute inset-0 p-2"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200'%3E%3Crect width='300' height='200' fill='%23e5e7eb'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='16' fill='%239ca3af'%3ENo Image%3C/text%3E%3C/svg%3E";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <div className="h-32 w-32 bg-blue-200 rounded-full flex items-center justify-center">
                              <PhotoIcon className="h-16 w-16 text-blue-500" />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Caratteristiche veicolo */}
                      <div className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Caratteristiche veicolo</h4>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-gray-500">Marca</p>
                            <p className="font-medium">{selectedVehicle.marca?.descrizione || selectedVehicle.brand || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Modello</p>
                            <p className="font-medium">{selectedVehicle.modello || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Allestimento</p>
                            <p className="font-medium">{selectedVehicle.allestimento || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Alimentazione</p>
                            <p className="font-medium">{selectedVehicle.alimentazione || '-'}</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-500">Trasmissione</p>
                            <p className="font-medium">{selectedVehicle.trasmissione || '-'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Colonna destra: Configura l'offerta + Dettagli */}
                    <div className="sm:w-2/3">
                      {/* Configura l'offerta */}
                      <div className="mb-4">
                        <h4 className="text-lg font-semibold text-gray-900 mb-3">Configura l'offerta</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                          {/* Durata */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Durata</label>
                            <select
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={durataFilter}
                              onChange={(e) => {
                                setDurataFilter(e.target.value);
                                setCurrentFilterIndex(0);
                                setShowProvvigioni(false);
                                setProvvigioniData(null);
                              }}
                            >
                              {[...new Set(vehicleOfferte.map(o => o.durata))]
                                .sort((a, b) => a - b)
                                .map(durata => (
                                  <option key={durata} value={durata.toString()}>
                                    {durata} mesi
                                  </option>
                                ))}
                            </select>
                          </div>
                          
                          {/* Chilometri annui */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Chilometri annui</label>
                            <select
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={kmAnnuiFilter}
                              onChange={(e) => {
                                setKmAnnuiFilter(e.target.value);
                                setCurrentFilterIndex(0);
                                setShowProvvigioni(false);
                                setProvvigioniData(null);
                              }}
                            >
                              {[...new Set(vehicleOfferte.map(o => o.kmAnnui))]
                                .sort((a, b) => a - b)
                                .map(km => (
                                  <option key={km} value={km.toString()}>
                                    {parseInt(km).toLocaleString()} km
                                  </option>
                                ))}
                            </select>
                          </div>
                          
                          {/* Anticipo */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Anticipo</label>
                            <select
                              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                              value={anticipoFilter}
                              onChange={(e) => {
                                setAnticipoFilter(e.target.value);
                                setCurrentFilterIndex(0);
                                setShowProvvigioni(false);
                                setProvvigioniData(null);
                              }}
                            >
                              <option value="">Tutti gli anticipi</option>
                              {[...new Set(vehicleOfferte
                                .filter(offerta => 
                                  (durataFilter ? offerta.durata.toString() === durataFilter : true) &&
                                  (kmAnnuiFilter ? offerta.kmAnnui.toString() === kmAnnuiFilter : true)
                                )
                                .map(o => o.anticipo || 0))]
                                .sort((a, b) => a - b)
                                .map(anticipo => (
                                  <option key={anticipo} value={anticipo.toString()}>
                                    {formatPrezzo(anticipo)}
                                  </option>
                                ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Dettagli offerta selezionata */}
                      <div className="border-t border-gray-200 pt-4">
                        {getFilteredOfferte().length > 0 ? (
                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <h4 className="text-lg font-semibold text-gray-900">
                                Offerta {currentFilterIndex + 1} di {getFilteredOfferte().length}
                              </h4>
                              <div className="flex">
                                <button 
                                  onClick={() => navigateOfferte('prev')}
                                  className="mr-2 inline-flex items-center p-1 border border-gray-300 rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                  disabled={getFilteredOfferte().length <= 1}
                                >
                                  <ChevronLeftIcon className="h-5 w-5" />
                                </button>
                                <button 
                                  onClick={() => navigateOfferte('next')}
                                  className="inline-flex items-center p-1 border border-gray-300 rounded-full shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
                                  disabled={getFilteredOfferte().length <= 1}
                                >
                                  <ChevronRightIcon className="h-5 w-5" />
                                </button>
                              </div>
                            </div>
                            
                            {/* Dettagli offerta corrente */}
                            {getCurrentOfferta() && (
                              <div className="bg-blue-50 rounded-lg p-4">
                                {/* Canone mensile in evidenza */}
                                <div className="flex items-end justify-between bg-white border border-blue-100 rounded-lg px-4 py-3 mb-4 shadow-sm">
                                  <div>
                                    <p className="text-xs uppercase tracking-wide text-gray-500 font-medium">Canone Mensile</p>
                                    <p className="text-3xl font-bold text-blue-600 leading-tight">
                                      {formatPrezzo(getCurrentOfferta().canone)}
                                      <span className="text-sm font-normal text-gray-500 ml-1">IVA escl.</span>
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-gray-500">Anticipo</p>
                                    <p className="text-lg font-semibold text-gray-800">
                                      {getCurrentOfferta().anticipo !== undefined && getCurrentOfferta().anticipo !== null
                                        ? formatPrezzo(getCurrentOfferta().anticipo)
                                        : formatPrezzo(0)}
                                      <span className="text-xs font-normal text-gray-500 ml-1">IVA escl.</span>
                                    </p>
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                  <div>
                                    <p className="text-sm text-gray-500">Società di Noleggio</p>
                                    <p className="font-medium">
                                      {getCurrentOfferta().societaNoleggio?.nome || '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Stato Disponibilità</p>
                                    <p className="font-medium">
                                      {getCurrentOfferta().statoDisponibilita || 'Ordine'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Tempi di consegna</p>
                                    <p className="font-medium">
                                      {(() => {
                                        const stato = (getCurrentOfferta().statoDisponibilita || '').toLowerCase();
                                        const settimane = Number(getCurrentOfferta().settimaneConsegnaVettura) || 0;
                                        // Priorità al tempo di consegna dichiarato nella singola offerta
                                        if (settimane > 0) return `${settimane} settiman${settimane === 1 ? 'a' : 'e'}`;
                                        if (stato.includes('pronta') || stato.includes('stock')) return 'Pronta consegna';
                                        return 'Da definire';
                                      })()}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Durata</p>
                                    <p className="font-medium">
                                      {getCurrentOfferta().durata ? `${getCurrentOfferta().durata} mesi` : '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Chilometri annui</p>
                                    <p className="font-medium">
                                      {getCurrentOfferta().kmAnnui ? `${parseInt(getCurrentOfferta().kmAnnui).toLocaleString()} km` : '-'}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Tipo Offerta</p>
                                    <p className="font-medium flex items-center gap-2">
                                      {(() => {
                                        const tc = (getCurrentOfferta().tipoCliente || getCurrentOfferta().tipo_cliente || '').toLowerCase();
                                        if (tc === 'privato') return 'Offerta per privato';
                                        if (tc === 'azienda') return 'Offerta per azienda';
                                        return '—';
                                      })()}
                                      {isUsato(getCurrentOfferta()) && (
                                        <span className="inline-block bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-xs font-semibold">Usato</span>
                                      )}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Penale Kasko</p>
                                    <p className="font-medium">
                                      {formatPenaleValue(getCurrentOfferta().penaleKasko)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Penale RCA</p>
                                    <p className="font-medium">
                                      {formatPenaleValue(getCurrentOfferta().penaleRCA)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-sm text-gray-500">Penale Furto</p>
                                    <p className="font-medium">
                                      {formatPenaleValue(getCurrentOfferta().penaleFurto)}
                                    </p>
                                  </div>
                                  {/* Campo provvigione nascosto, solo per elaborazioni interne */}
                                  <input 
                                    type="hidden" 
                                    name="provvigione" 
                                    value={getCurrentOfferta().provvigione || 0} 
                                  />
                                  
                                  {/* Pulsante piccolo per mostrare provvigioni - poco visibile */}
                                  <div className="col-span-full flex justify-end">
                                    <button
                                      onClick={toggleProvvigioni}
                                      className="text-xs text-gray-400 hover:text-gray-600 opacity-50 hover:opacity-100 transition-all duration-200"
                                      title="Visualizza calcolo provvigioni"
                                    >
                                      {showProvvigioni ? '👁️ Nascondi' : '💰'}
                                    </button>
                                  </div>
                                </div>
                                
                                {/* Sezione provvigioni - mostrata solo se richiesta */}
                                {showProvvigioni && (
                                  <div className="mt-4 border-t border-gray-200 pt-4">
                                    <div className="flex items-center justify-between mb-3">
                                      {loadingProvvigioni && (
                                        <div className="flex items-center">
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                                          <span className="text-xs text-gray-500">Calcolo...</span>
                                        </div>
                                      )}
                                    </div>
                                    
                                    {provvigioniData && !loadingProvvigioni && (
                                      <div>
                                        {provvigioniData.errore ? (
                                          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                                            <p className="text-red-800 font-medium text-sm">Errore nel calcolo</p>
                                            <p className="text-red-600 text-xs mt-1">{provvigioniData.errore}</p>
                                          </div>
                                        ) : (
                                          <div>
                                            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                                              <div className="flex justify-between items-center">
                                                <span className="text-sm font-medium text-green-800">
                                                  {provvigioniData.provvigioneTotale ? 'Totale Provvigioni:' : 'Tua Provvigione:'}
                                                </span>
                                                <span className="text-lg font-bold text-green-600">
                                                  {formatCurrency(provvigioniData.provvigioneTotale || provvigioniData.provvigionePersonale || 0)}
                                                </span>
                                              </div>
                                            </div>
                                            
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-gray-500">Nessuna offerta disponibile con i filtri selezionati.</p>
                            <button 
                              onClick={() => {
                                setDurataFilter('');
                                setKmAnnuiFilter('');
                                setTipoClienteFilter('');
                                setAnticipoFilter('');
                                setCurrentFilterIndex(0);
                              }}
                              className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              Resetta i filtri
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Footer */}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      const offertaSelezionata = getCurrentOfferta();
                      if (offertaSelezionata) {
                        openSelezionePraticaModal(offertaSelezionata);
                      } else {
                        toast.warning('Seleziona prima un\'offerta valida.');
                      }
                    }}
                  >
                    Richiedi preventivo
                  </button>
                  <button
                    type="button"
                    disabled={scaricandoPdf}
                    className="w-full inline-flex justify-center items-center gap-1 rounded-md border border-transparent shadow-sm px-4 py-2 bg-emerald-600 text-base font-medium text-white hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={scaricaPreventivoOffertaPdf}
                  >
                    {scaricandoPdf ? 'Generazione…' : 'Scarica preventivo PDF'}
                  </button>
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => {
                      setShowModal(false);
                      setSelectedVehicle(null);
                      setVehicleOfferte([]);
                      setDurataFilter('');
                      setKmAnnuiFilter('');
                      setTipoClienteFilter('');
                      setAnticipoFilter('');
                      setCurrentFilterIndex(0);
                      setShowProvvigioni(false);
                      setProvvigioniData(null);
                    }}
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Modale Selezione Pratica */}
        {showSelezionePraticaModal && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              {/* Overlay */}
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
              
              {/* Modal */}
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                {/* Header */}
                <div className="bg-white px-4 pt-4 pb-4 sm:p-6 sm:pb-4 border-b">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Associa Preventivo
                    </h3>
                    <button
                      onClick={() => setShowSelezionePraticaModal(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>
                </div>
                
                {/* Body */}
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                  {/* Riepilogo offerta selezionata */}
                  {offertaPerPreventivo && (
                    <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Offerta Selezionata</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Vettura:</span>
                          <p className="font-medium">
                            {offertaPerPreventivo.vettura?.marca?.descrizione || offertaPerPreventivo.vettura?.brand} {offertaPerPreventivo.vettura?.modello}
                          </p>
                        </div>
                        <div>
                          <span className="text-gray-500">Canone:</span>
                          <p className="font-medium text-blue-600">{formatPrezzo(offertaPerPreventivo.canone)}/mese</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Durata:</span>
                          <p className="font-medium">{offertaPerPreventivo.durata} mesi</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Anticipo:</span>
                          <p className="font-medium">{formatPrezzo(offertaPerPreventivo.anticipo || 0)}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Tab Navigation */}
                  <div className="mb-6">
                    <div className="border-b border-gray-200">
                      <nav className="-mb-px flex space-x-8">
                        <button
                          onClick={() => handleTabChange('pratica')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            modalTab === 'pratica'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Pratica Esistente
                        </button>
                        <button
                          onClick={() => handleTabChange('cliente')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            modalTab === 'cliente'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Cliente
                        </button>
                      </nav>
                    </div>
                  </div>

                  {/* Tab Content - Pratica */}
                  {modalTab === 'pratica' && (
                    <div>
                      {/* Ricerca pratiche */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cerca Pratica
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Cerca per numero pratica, nome cliente..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                            value={searchPratiche}
                            onChange={(e) => setSearchPratiche(e.target.value)}
                          />
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        </div>
                      </div>
                      
                      {/* Lista pratiche */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Seleziona Pratica (escluse quelle in attesa approvazione cliente o superiore)
                        </label>
                        {loadingPratiche ? (
                          <div className="text-center py-4">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                            <p className="mt-2 text-gray-600">Caricamento pratiche...</p>
                          </div>
                        ) : praticheFiltered.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            Nessuna pratica trovata
                          </div>
                        ) : (
                          <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md">
                            {praticheFiltered.map((pratica) => (
                              <div
                                key={pratica.id}
                                className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                                  praticaSelezionata === pratica.id ? 'bg-blue-50 border-blue-200' : ''
                                }`}
                                onClick={() => setPraticaSelezionata(pratica.id)}
                              >
                                <div className="flex justify-between items-start">
                                  <div>
                                    <div className="font-medium text-gray-900">
                                      {pratica.codicePratica || `Pratica #${pratica.id}`}
                                    </div>
                                    <div className="text-sm text-gray-600">
                                      {pratica.cliente?.tipo_cliente === 'privato' 
                                        ? `${pratica.cliente?.nome} ${pratica.cliente?.cognome}`
                                        : pratica.cliente?.ragione_sociale
                                      }
                                    </div>
                                    <div className="text-xs text-gray-500 flex items-center gap-2">
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        pratica.statoPratica === 'pratica_avviata' ? 'bg-blue-100 text-blue-700' :
                                        pratica.statoPratica === 'attesa_preventivo' ? 'bg-yellow-100 text-yellow-700' :
                                        pratica.statoPratica === 'valutazione_cliente' ? 'bg-purple-100 text-purple-700' :
                                        pratica.statoPratica === 'attesa_approvazione_cliente' ? 'bg-orange-100 text-orange-700' :
                                        'bg-gray-100 text-gray-700'
                                      }`}>
                                        {pratica.statoPratica === 'pratica_avviata' ? 'Pratica Avviata' :
                                         pratica.statoPratica === 'attesa_preventivo' ? 'Attesa Preventivo' :
                                         pratica.statoPratica === 'valutazione_cliente' ? 'Valutazione Cliente' :
                                         pratica.statoPratica === 'attesa_approvazione_cliente' ? 'Attesa Approvazione' :
                                         pratica.statoPratica || pratica.stato_pratica}
                                      </span>
                                      <span>Creata: {formatDateShort(pratica.createdAt)}</span>
                                    </div>
                                  </div>
                                  {praticaSelezionata === pratica.id && (
                                    <div className="text-blue-600">
                                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tab Content - Cliente */}
                  {modalTab === 'cliente' && (
                    <div>
                      {/* Ricerca clienti */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Cerca Cliente
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Cerca per nome, email, codice fiscale..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
                            value={searchClienti}
                            onChange={(e) => setSearchClienti(e.target.value)}
                          />
                          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                        </div>
                      </div>

                      {/* Pulsante nuovo cliente */}
                      <div className="mb-4">
                        <button
                          onClick={() => setShowNuovoClienteForm(!showNuovoClienteForm)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {showNuovoClienteForm ? 'Nascondi Form' : 'Nuovo Cliente'}
                        </button>
                      </div>

                      {/* Form nuovo cliente */}
                      {showNuovoClienteForm && (
                        <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <h5 className="text-lg font-medium text-gray-900 mb-4">Crea Nuovo Cliente</h5>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Cliente</label>
                              <select
                                value={nuovoClienteData.tipo_cliente}
                                onChange={(e) => setNuovoClienteData({...nuovoClienteData, tipo_cliente: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                              >
                                <option value="privato">Privato</option>
                                <option value="azienda">Azienda</option>
                              </select>
                            </div>
                            
                            {nuovoClienteData.tipo_cliente === 'privato' ? (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                                  <input
                                    type="text"
                                    value={nuovoClienteData.nome}
                                    onChange={(e) => setNuovoClienteData({...nuovoClienteData, nome: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                                  <input
                                    type="text"
                                    value={nuovoClienteData.cognome}
                                    onChange={(e) => setNuovoClienteData({...nuovoClienteData, cognome: e.target.value})}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                </div>
                              </>
                            ) : (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ragione Sociale *</label>
                                <input
                                  type="text"
                                  value={nuovoClienteData.ragione_sociale}
                                  onChange={(e) => setNuovoClienteData({...nuovoClienteData, ragione_sociale: e.target.value})}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale</label>
                              <input
                                type="text"
                                value={nuovoClienteData.codice_fiscale}
                                onChange={(e) => setNuovoClienteData({...nuovoClienteData, codice_fiscale: e.target.value.toUpperCase()})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                maxLength={16}
                                placeholder="16 caratteri (es. RSSMRA80A01H501M)"
                              />
                            </div>
                            
                            {nuovoClienteData.tipo_cliente === 'azienda' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA</label>
                                <input
                                  type="text"
                                  value={nuovoClienteData.partita_iva}
                                  onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, '');
                                    setNuovoClienteData({...nuovoClienteData, partita_iva: value});
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  maxLength={11}
                                  placeholder="11 cifre (es. 12345678901)"
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                              <input
                                type="email"
                                value={nuovoClienteData.email}
                                onChange={(e) => setNuovoClienteData({...nuovoClienteData, email: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="es. mario.rossi@email.com"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Telefono *</label>
                              <input
                                type="tel"
                                value={nuovoClienteData.telefono}
                                onChange={(e) => setNuovoClienteData({...nuovoClienteData, telefono: e.target.value})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="es. 3331234567"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Lista clienti */}
                      {!showNuovoClienteForm && (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Seleziona Cliente (se ha pratiche valide le userà, altrimenti ne creerà una nuova)
                          </label>
                          {loadingClienti ? (
                            <div className="text-center py-4">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                              <p className="mt-2 text-gray-600">Caricamento clienti...</p>
                            </div>
                          ) : clientiFiltered.length === 0 ? (
                            <div className="text-center py-4 text-gray-500">
                              Nessun cliente trovato
                            </div>
                          ) : (
                            <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-md">
                              {clientiFiltered.map((cliente) => (
                                <div
                                  key={cliente.id}
                                  className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${
                                    clienteSelezionato === cliente.id ? 'bg-blue-50 border-blue-200' : ''
                                  }`}
                                  onClick={() => setClienteSelezionato(cliente.id)}
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="font-medium text-gray-900">
                                        {cliente.tipo_cliente === 'privato' 
                                          ? `${cliente.nome} ${cliente.cognome}`
                                          : cliente.ragione_sociale
                                        }
                                      </div>
                                      <div className="text-sm text-gray-600">
                                        {cliente.email && `Email: ${cliente.email}`}
                                      </div>
                                      <div className="text-xs text-gray-500">
                                        {cliente.codice_fiscale && `CF: ${cliente.codice_fiscale}`}
                                        {cliente.partita_iva && ` | P.IVA: ${cliente.partita_iva}`}
                                      </div>
                                    </div>
                                    {clienteSelezionato === cliente.id && (
                                      <div className="text-blue-600">
                                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Footer */}
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  {modalTab === 'pratica' && (
                    <button
                      type="button"
                      className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={associaOffertaAPratica}
                      disabled={!praticaSelezionata || loadingCreazione}
                    >
                      {loadingCreazione ? 'Associando...' : 'Associa a Pratica'}
                    </button>
                  )}
                  
                  {modalTab === 'cliente' && (
                    <>
                      {showNuovoClienteForm ? (
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={creaClienteEPratica}
                          disabled={(!nuovoClienteData.nome && !nuovoClienteData.ragione_sociale) || loadingCreazione}
                        >
                          {loadingCreazione ? 'Creando...' : 'Crea Cliente e Pratica'}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={associaOffertaACliente}
                          disabled={!clienteSelezionato || loadingCreazione}
                        >
                          {loadingCreazione ? 'Associando...' : 'Associa a Cliente'}
                        </button>
                      )}
                    </>
                  )}
                  
                  <button
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={() => setShowSelezionePraticaModal(false)}
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffertePublicPage; 